import { ColorEnum } from '@/constants/color'
import { PriceValue, ShiftCloseoutReconciliation } from '@/features/shiftCloseouts/types'
import Typography from '@/features/ui/typography'
import { formatPrice } from '@/utils/priceHelper'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface OverviewProps {
    reconciliation: ShiftCloseoutReconciliation
    totalAmount: PriceValue
}

const Overview: FC<OverviewProps> = ({ reconciliation, totalAmount }) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, { state: reconciliation.state })
    const difference = reconciliation.isComplete
        ? formatPrice(
              reconciliation.totalDifference.amount,
              reconciliation.totalDifference.currency,
              reconciliation.state === 'surplus'
          )
        : '—'

    return (
        <View style={styles.section}>
            <Typography.Heading variant="h5" style={styles.sectionHeading}>
                {t('shiftCloseouts.overview.heading')}
            </Typography.Heading>

            <View style={styles.cards}>
                <View style={[styles.card, styles.salesCard]}>
                    <Typography.Description font="bold" size="tiny" style={styles.cardLabel}>
                        {t('shiftCloseouts.overview.totalSales').toUpperCase()}
                    </Typography.Description>
                    <Typography.Heading variant="h3" numberOfLines={1}>
                        {formatPrice(totalAmount.amount, totalAmount.currency)}
                    </Typography.Heading>
                </View>

                <View style={[styles.card, styles.differenceCard]}>
                    <Typography.Description font="bold" size="tiny" style={styles.cardLabel}>
                        {t('shiftCloseouts.overview.difference').toUpperCase()}
                    </Typography.Description>
                    <Typography.Heading variant="h3" numberOfLines={1} style={styles.difference}>
                        {difference}
                    </Typography.Heading>
                </View>
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet({
    section: {
        gap: 8,
    },
    sectionHeading: {
        color: ColorEnum.GRAY_900,
    },
    cards: {
        flexDirection: 'row',
        gap: 16,
    },
    card: {
        flex: 1,
        minWidth: 0,
        gap: 4,
        padding: 16,
        borderRadius: 12,
    },
    salesCard: {
        backgroundColor: ColorEnum.GRAY_200,
    },
    differenceCard: {
        variants: {
            state: {
                balanced: { backgroundColor: ColorEnum.EMERALD_100 },
                incomplete: { backgroundColor: ColorEnum.VIOLET_100 },
                mismatch: { backgroundColor: ColorEnum.EMERALD_100 },
                shortage: { backgroundColor: ColorEnum.Brick100 },
                surplus: { backgroundColor: ColorEnum.Yellow100 },
            },
        },
    },
    cardLabel: {
        color: ColorEnum.JET_500,
        letterSpacing: 0.5,
    },
    difference: {
        variants: {
            state: {
                balanced: { color: ColorEnum.EMERALD_700 },
                incomplete: { color: ColorEnum.GRAY_600 },
                mismatch: { color: ColorEnum.EMERALD_700 },
                shortage: { color: ColorEnum.BRICK_500 },
                surplus: { color: ColorEnum.YELLOW_600 },
            },
        },
    },
})

export default Overview
